import { useEffect, useId, useRef, useState } from 'react'
import { createUniver, LocaleType, mergeLocales } from '@univerjs/presets'
import { UniverSheetsCorePreset } from '@univerjs/preset-sheets-core'
import zhCN from '@univerjs/preset-sheets-core/locales/zh-CN'
import '@univerjs/preset-sheets-core/lib/index.css'
import type { WorkbookTraceIndex } from './types'
import type { ManualCellTarget } from './workbookData'

interface SourceCellTarget {
  sheetName?: string
  cellRange?: string
}

export interface UniverHostProps {
  workbookData: Record<string, unknown>
  workbookKey?: string
  rowMaps?: WorkbookTraceIndex
  editableCells?: Record<string, ManualCellTarget>
  activeSheet?: string
  activeRecordId?: string
  selectableRecordIds?: ReadonlySet<string>
  sourceCell?: SourceCellTarget
  onRecordSelect?: (recordId: string) => void
  onSheetChange?: (sheetName: string) => void
  onManualCellChange?: (edit: {
    key: string
    sheetName: string
    row: number
    column: number
    value: string
    recordId?: string
  }) => void
  className?: string
}

function safeId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_')
}

export function UniverHost({
  workbookData,
  workbookKey,
  rowMaps,
  editableCells,
  activeSheet,
  activeRecordId,
  selectableRecordIds,
  sourceCell,
  onRecordSelect,
  onSheetChange,
  onManualCellChange,
  className = '',
}: UniverHostProps) {
  const rawId = useId()
  const hostId = `univer-${safeId(rawId)}`
  const apiRef = useRef<any>(null)
  const workbookRef = useRef<any>(null)
  const rowMapsRef = useRef(rowMaps)
  const editableCellsRef = useRef(editableCells)
  const activeRecordIdRef = useRef(activeRecordId)
  const selectableRecordIdsRef = useRef(selectableRecordIds)
  const onRecordSelectRef = useRef(onRecordSelect)
  const onSheetChangeRef = useRef(onSheetChange)
  const onManualCellChangeRef = useRef(onManualCellChange)
  const [error, setError] = useState<string | null>(null)

  rowMapsRef.current = rowMaps
  editableCellsRef.current = editableCells
  activeRecordIdRef.current = activeRecordId
  selectableRecordIdsRef.current = selectableRecordIds
  onRecordSelectRef.current = onRecordSelect
  onSheetChangeRef.current = onSheetChange
  onManualCellChangeRef.current = onManualCellChange

  useEffect(() => {
    let disposed = false
    let univer: any
    let workbook: any
    const disposables: any[] = []

    async function mount() {
      try {
        setError(null)
        const created = createUniver({
          locale: LocaleType.ZH_CN,
          locales: { [LocaleType.ZH_CN]: mergeLocales(zhCN) },
          presets: [UniverSheetsCorePreset({
            container: hostId,
            toolbar: false,
            formulaBar: false,
            footer: {
              sheetBar: true,
              statisticBar: false,
              menus: false,
              zoomSlider: false,
              addSheetButtonConfig: { show: false },
            },
            contextMenu: false,
          })],
        })
        univer = created.univer
        const api = created.univerAPI
        apiRef.current = api
        workbook = api.createWorkbook(workbookData as any)
        workbookRef.current = workbook
        await enableWorkbookEditing(workbook)

        const handleLifecycle = async (params: any) => {
          if (params?.stage !== 'rendered' && params?.stage !== api.Enum?.LifecycleStages?.Rendered) return
          const current = api.getActiveWorkbook?.() || workbook
          await enableWorkbookEditing(current)
          if (!disposed) {
            if (activeSheet) activateSheet(current, activeSheet, sourceCell, activeRecordIdRef.current, rowMapsRef.current)
          }
        }
        const handleBeforeEditStart = (params: any) => {
          const worksheet = params?.worksheet
          if (!worksheet || typeof params?.row !== 'number' || typeof params?.column !== 'number') return
          const name = worksheet.getSheetName?.() || ''
          const target = editableCellsRef.current?.[cellKey(name, params.row, params.column)]
          if (!target) params.cancel = true
        }
        const handleCellClick = (params: any) => {
          const worksheet = params?.worksheet
          const row = params?.row
          const column = params?.column
          if (!worksheet || typeof row !== 'number') return
          const name = worksheet.getSheetName?.() || worksheet.getSheet?.()?.getSheetName?.() || ''
          // The manual-review column is a real editing surface. Do not turn its
          // click into a record navigation event, otherwise React would update
          // the trace panel and move the workbook viewport before editing starts.
          if (typeof column === 'number' && editableCellsRef.current?.[cellKey(name, row, column)]) return
          const recordId = rowMapsRef.current?.[name]?.[String(row)]
          if (recordId && (!selectableRecordIdsRef.current || selectableRecordIdsRef.current.has(recordId))) {
            onRecordSelectRef.current?.(recordId)
          }
        }
        const handleSheetActivated = (params: any) => {
          const worksheet = params?.activeSheet || params?.worksheet || params?.sheet
          const name = worksheet?.getSheetName?.() || params?.sheetName || params?.name
          if (name) onSheetChangeRef.current?.(name)
        }
        const handleSheetValueChanged = (params: any) => {
          for (const range of params?.effectedRanges || []) {
            const sheetName = range?.getSheetId?.() || ''
            const startRow = range?.getRow?.()
            const startColumn = range?.getColumn?.()
            const lastRow = range?.getLastRow?.()
            const lastColumn = range?.getLastColumn?.()
            if (typeof startRow !== 'number' || typeof startColumn !== 'number') continue
            const values = range?.getValues?.() || []
            const endRow = typeof lastRow === 'number' ? lastRow : startRow
            const endColumn = typeof lastColumn === 'number' ? lastColumn : startColumn
            const worksheet = workbook?.getSheetBySheetId?.(sheetName)
            const resolvedName = worksheet?.getSheetName?.() || sheetName
            for (let row = startRow; row <= endRow; row += 1) {
              for (let column = startColumn; column <= endColumn; column += 1) {
                const target = editableCellsRef.current?.[cellKey(resolvedName, row, column)]
                if (!target) continue
                const value = values[row - startRow]?.[column - startColumn]
                onManualCellChangeRef.current?.({
                  key: target.key,
                  sheetName: resolvedName,
                  row,
                  column,
                  value: value == null ? '' : String(value),
                  recordId: target.recordId,
                })
              }
            }
          }
        }
        disposables.push(api.addEvent(api.Event.LifeCycleChanged, handleLifecycle))
        disposables.push(api.addEvent(api.Event.BeforeSheetEditStart, handleBeforeEditStart))
        disposables.push(api.addEvent(api.Event.CellClicked, handleCellClick))
        disposables.push(api.addEvent(api.Event.SheetValueChanged, handleSheetValueChanged))
        for (const eventName of ['ActiveSheetChanged', 'SheetActivated', 'CurrentSheetChanged', 'SheetChanged']) {
          const event = (api.Event as Record<string, any> | undefined)?.[eventName]
          if (event) disposables.push(api.addEvent(event, handleSheetActivated))
        }
      } catch (mountError) {
        console.error(mountError)
        setError(mountError instanceof Error ? mountError.message : String(mountError))
      }
    }

    mount()
    return () => {
      disposed = true
      disposables.forEach((item) => item?.dispose?.())
      try { workbook?.dispose?.() } catch { /* best effort */ }
      try { univer?.dispose?.() } catch { /* best effort */ }
      apiRef.current = null
      workbookRef.current = null
    }
    // Recreating the workbook is intentional when the filtered snapshot changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbookKey, hostId])

  useEffect(() => {
    const api = apiRef.current
    const workbook = workbookRef.current
    if (!api || !workbook || !activeSheet) return
    activateSheet(workbook, activeSheet, sourceCell, activeRecordId, rowMaps)
  }, [activeSheet, activeRecordId, rowMaps, sourceCell])

  return (
    <div className={`univer-host ${className}`}>
      {error ? <div className="univer-error">Univer 加载失败：{error}</div> : null}
      <div id={hostId} className="univer-canvas" data-testid="univer-canvas" />
    </div>
  )
}

async function enableWorkbookEditing(workbook: any) {
  try {
    // Keep the public facade permission and the legacy workbook switch in sync.
    // The latter is still needed by the 0.25 runtime before the first rendered
    // lifecycle event has initialized sheet-level permissions.
    workbook?.setEditable?.(true)
    await workbook?.getWorkbookPermission?.().setEditable?.()
  } catch (permissionError) {
    console.debug('Unable to enable Univer editing', permissionError)
  }
}

function cellKey(sheetName: string, row: number, column: number) {
  return `${sheetName}:${row}:${column}`
}

function activateSheet(workbook: any, sheetName: string, sourceCell?: SourceCellTarget, activeRecordId?: string, rowMaps?: WorkbookTraceIndex) {
  const mappedSheetName = activeRecordId && rowMaps
    ? Object.entries(rowMaps).find(([, map]) => Object.values(map).includes(activeRecordId))?.[0]
    : undefined
  // Respect an explicit auxiliary view selection. Record auto-navigation is only
  // applied when the requested sheet is the record's own parameter sheet.
  const targetSheetName = !sheetName || sheetName === mappedSheetName ? (mappedSheetName || sheetName) : sheetName
  const sheet = workbook.getSheetByName?.(targetSheetName)
  if (!sheet) return
  workbook.setActiveSheet?.(sheet)
  const mappedRow = activeRecordId && targetSheetName === mappedSheetName && rowMaps?.[targetSheetName]
    ? Object.entries(rowMaps[targetSheetName]).find(([, recordId]) => recordId === activeRecordId)?.[0]
    : undefined
  if (mappedRow != null && !sourceCell?.cellRange) {
    const row = Number(mappedRow)
    const applyRecordTarget = () => {
      try {
        const range = sheet.getRange?.(row, 0, 1, 9)
        if (!range) return
        const currentRange = sheet.getActiveRange?.()
        const currentColumn = currentRange?.getColumn?.()
        sheet.setActiveRange?.(range)
        sheet.setActiveSelection?.(range)
        range.activate?.()
        sheet.scrollToCell?.(row, typeof currentColumn === 'number' ? currentColumn : 0, 0)
      } catch (error) {
        console.debug('Unable to activate audit record', error)
      }
    }
    applyRecordTarget()
    window.setTimeout(applyRecordTarget, 120)
  }
  if (sourceCell?.cellRange) {
    const sourceSheet = workbook.getSheetByName?.(sourceCell.sheetName || sheetName) || sheet
    const applyCellTarget = () => {
      try {
        const range = sourceSheet.getRange(sourceCell.cellRange)
        sourceSheet.setActiveRange?.(range)
        sourceSheet.setActiveSelection?.(range)
        range.activate?.()
        const row = range.getRow?.()
        const column = range.getColumn?.()
        if (typeof row === 'number' && typeof column === 'number') {
          sourceSheet.scrollToCell?.(row, column, 0)
        }
      } catch (error) {
        console.debug('Unable to activate source cell', error)
      }
    }
    applyCellTarget()
    // The first canvas render can reset the viewport; re-apply after it settles.
    window.setTimeout(applyCellTarget, 120)
  }
}
